import { actionsNetwork } from './network';

/**
 * Things only the web app sees, for Shovel's activity log (backend POST /api/me/actions): the app opening, dialogs
 * and views. Everything the student changes is logged by the backend and the event-service themselves, so it must not
 * be sent from here as well. The backend drops names it doesn't know (ClientActionService.ALLOWED).
 */
export type ClientAction =
  | 'app_opened'
  | 'autoplan_dialog_opened'
  | 'unplan_dialog_opened'
  | 'estimate_box_opened'
  | 'cushion_viewed'
  | 'streak_opened'
  | 'membership_viewed'
  | 'paywall_viewed'
  | 'resubscribe_clicked'
  | 'cancel_flow_opened'
  | 'demo_completed'
  // Browsing, for the admin Usage page.
  | 'page_viewed'
  | 'task_details_opened'
  | 'course_details_opened'
  | 'assistant_opened'
  | 'share_calendar_opened'
  | 'calendar_view_changed'
  | 'calendar_moved'
  // The timer's chime and desktop notice (timer-alerts spec): shown (props: moment, chime, notice, late), a button or
  // click on the notice (action), and the ask for the browser's permission.
  | 'session_ping_clicked'
  | 'timer_alert_shown'
  | 'timer_alert_clicked'
  | 'notice_permission_asked'
  | 'notice_permission_granted'
  | 'notice_permission_denied'
  | 'notice_permission_dismissed';

/** Fire and forget: never awaited, never fails, and only sent when someone is signed in (then it returns true). */
export const reportAction = (event: ClientAction, props?: Record<string, string | number | boolean>) => {
  if (!actionsNetwork.headers?.Authorization) return false;
  actionsNetwork.post('/me/actions', props ? { event, props } : { event }).catch(() => undefined);
  return true;
};

/** The server's id for a change it just saved (X-Shovel-Action), for linking an Undo to it. */
export const actionIdOf = (response: { headers?: any } | null | undefined): string | undefined => {
  const value = response?.headers?.['x-shovel-action'];
  return typeof value === 'string' ? value : undefined;
};

/** For a request that is a later step of the same change (Auto-plan's Replan, a task made on the calendar and its
 * session): one id, so one Undo marks the lot. */
export const sameAction = (actionId?: string) => (actionId ? { 'X-Shovel-Action': actionId } : undefined);

/** For an Undo request: the change it takes back. */
export const undoOf = (actionId?: string) => (actionId ? { 'X-Shovel-Undo-Of': actionId } : undefined);

// The pages the admin Usage page counts, by path (the server keeps only these names: ClientActionService.PAGES).
const PAGES: [RegExp, string][] = [
  [/^\/\d+\/home/, 'calendar'],
  [/^\/\d+\/tasks/, 'tasks'],
  [/^\/\d+\/charts/, 'charts'],
  [/^\/\d+\/term/, 'term'],
  [/^\/\d+\/awake-time/, 'awake_time'],
  [/^\/\d+\/study-time/, 'study_time'],
  [/^\/gpa-calculator/, 'gpa_calculator'],
  [/^\/settings/, 'settings'],
  [/^\/connect/, 'connect_ai_app']
];

let lastPage: string | undefined;

/**
 * A visit to one of the counted pages; staying on it (or moving within it) is not another visit. The page the app
 * opens on shows before sign-in has finished, so nothing can be sent yet: it stays uncounted until the app has opened
 * (common/operations calls this again then).
 */
export const reportPageView = (pathname: string) => {
  const page = PAGES.find(([path]) => path.test(pathname))?.[1];
  if (page === lastPage) return;
  if (page && !reportAction('page_viewed', { page })) return;
  lastPage = page;
};

let lastCalendarChange: string | undefined;

/**
 * A switch of calendar view or a move to other dates, once: FullCalendar can tell one switch twice (Petr's staging
 * check, 2026-10-20: every view switch counted double). `shown` names the view and dates now on screen.
 */
export const reportCalendarChange = (
  shown: string,
  event: 'calendar_view_changed' | 'calendar_moved',
  props: Record<string, string>
) => {
  if (shown === lastCalendarChange) return;
  lastCalendarChange = shown;
  reportAction(event, props);
};

// The calendar's views by the names the server keeps (ClientActionService.CALENDAR_VIEWS).
const CALENDAR_VIEWS: { [view: string]: string } = {
  cyclicWeek: 'days',
  timeGridWeek: 'week',
  dayGridMonth: 'month',
  listWeek: 'list',
  listDay: 'agenda'
};

export const calendarViewName = (view: string) => CALENDAR_VIEWS[view] || 'other';
